import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { logger } from "helpers/logger";

export const ErrorBoundaryContext = React.createContext({
  reportError: (error, errorInfo) => {},
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  componentDidMount() {
    logger.info("componentDidMount")
    window.addEventListener('error', this.handleGlobalError, true);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.hideReactErrorOverlay();
  }

  componentWillUnmount() {
    logger.info("componentWillUnmount")
    window.removeEventListener('error', this.handleGlobalError, true);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  hideReactErrorOverlay = () => {
    const removeOverlay = () => {
      const overlay = document.querySelector('iframe[style*="position: fixed"]');
      if (overlay) overlay.style.display = 'none';
    };
    setTimeout(removeOverlay, 0);
    setTimeout(removeOverlay, 100);
  };

  handleGlobalError = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.state.hasError) {
      const error = event.error || new Error(event.message);
      this.setState({
        hasError: true,
        error,
        errorInfo: { componentStack: error.stack || `\nError at ${event.filename}:${event.lineno}:${event.colno}` },
      });
    }

    this.hideReactErrorOverlay();
  };

  handleUnhandledRejection = (event) => {
    event.preventDefault();
    this.setState({
      hasError: true,
      error: event.reason || new Error('Unhandled Promise Rejection'),
      errorInfo: { componentStack: event.reason?.stack || '\nPromise rejection in async code' },
    });
    this.hideReactErrorOverlay();
  };

  reportError = (error, errorInfo) => {
    this.setState({
      hasError: true,
      error,
      errorInfo: errorInfo || { componentStack: error.stack },
    });
  };

  handleReload = () => window.location.reload();
  handleGoHome = () => (window.location.href = '/');

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5', p: 3 }}>
          <Paper elevation={3} sx={{ p: 4, maxWidth: 800, width: '100%', textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="error">Oops! Something went wrong</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We're sorry for the inconvenience. An unexpected error has occurred.
            </Typography>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3f3', borderRadius: 1, textAlign: 'left', overflow: 'auto', maxHeight: 400, border: '1px solid #ffcdd2' }}>
                <Typography variant="subtitle2" color="error" gutterBottom sx={{ fontWeight: 'bold' }}>Error Details (Development Only):</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#c62828', mb: 2, fontWeight: 600 }}>
                  {this.state.error.name || 'Error'}: {this.state.error.message}
                </Typography>
                {this.state.errorInfo?.componentStack && (
                  <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 2, borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                    {this.state.errorInfo.componentStack}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="contained" color="primary" onClick={this.handleReload}>Reload Page</Button>
              <Button variant="outlined" color="primary" onClick={this.handleGoHome}>Go to Home</Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return (
      <ErrorBoundaryContext.Provider value={{ reportError: this.reportError }}>
        {this.props.children}
      </ErrorBoundaryContext.Provider>
    );
  }
}

export default ErrorBoundary;
